import { forwardRef, useCallback, useMemo } from 'react';

import { isWeb } from '@tamagui/core';
import type { GestureReponderEvent, TamaguiComponent, TamaguiElement } from '@tamagui/core';
import { Linking } from 'react-native';
import type { PressableProps } from 'react-native';

import { ComponentWithViewTracking } from '../../helpers/analytics';
import type { AnalyticsProps } from '../../helpers/analytics';
import { useObservabilitySafe } from '../../helpers/useObservabilitySafe';

export type AnchorOrButtonProps = Pick<PressableProps, 'onPress'> &
  AnalyticsProps & {
    /**
     * The URL to navigate to. Providing this prop will make the component
     * render an anchor tag.
     */
    href?: string;
    /**
     * Where to display the linked URL.
     * See: https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a#target
     */
    target?: string;
    /**
     * The relationship of the linked resource to the current page.
     * See: https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/rel
     */
    rel?: string;
    /**
     * Optional function to override how the URL is opened on native. Default
     * behavior is to call `Linking.openURL` with the `href` prop and throw an
     * error if the URL cannot be opened.
     */
    openURL?: typeof Linking.openURL;
  };

export const useAnchorOrButtonPress = ({
  openURL,
  analyticsName,
  href,
  onPress,
}: Pick<AnchorOrButtonProps, 'href' | 'openURL' | 'analyticsName' | 'onPress'>) => {
  const handleURLNative: typeof openURL = useMemo(() => {
    if (isWeb) {
      return undefined;
    }

    if (openURL) {
      return openURL;
    }

    return async (href) => {
      const canOpen = await Linking.canOpenURL(href);
      if (!canOpen) {
        throw new Error(`Cannot open URL: ${href}`);
      }
      return Linking.openURL(href);
    };
  }, [openURL]);

  const { trackJourneyEvent } = useObservabilitySafe();

  return useCallback(
    (e: GestureReponderEvent) => {
      if (analyticsName) {
        const eventName = href ? 'linkClickEvent' : 'buttonClickEvent';
        trackJourneyEvent(
          'platform',
          eventName,
          eventName === 'linkClickEvent'
            ? {
                targetName: analyticsName,
              }
            : {
                targetName: analyticsName,
              },
        );
      }
      if (href && handleURLNative) {
        handleURLNative(href);
      }
      onPress?.(e);
    },
    [analyticsName, href, handleURLNative, onPress, trackJourneyEvent],
  );
};

/**
 * Factory function that returns a component that renders either a button or a
 * link based on the presence of an `href` prop.
 */
export const createAnchorOrButtonFrame = <StyledProps extends object>({
  AnchorFrame,
  ButtonFrame,
}: {
  /** Component to use to render the anchor container */
  AnchorFrame: TamaguiComponent;
  /** Component to use to render the button container */
  ButtonFrame: TamaguiComponent;
}) => {
  return forwardRef<
    TamaguiElement,
    StyledProps &
      AnchorOrButtonProps & {
        /**
         * Tag should always be `<button>` or `<a>`, never passed in
         */
        tag?: never;
      }
  >(function AnchorOrButtonFrame(
    { href, openURL, analyticsName, trackImpression, onPress, ...props },
    ref,
  ) {
    const handlePress = useAnchorOrButtonPress({ href, openURL, analyticsName, onPress });

    const Component =
      isWeb && href ? (
        <AnchorFrame href={href} onPress={handlePress} ref={ref} {...props} />
      ) : (
        <ButtonFrame onPress={handlePress} ref={ref} {...props} />
      );

    if (analyticsName && trackImpression) {
      return (
        <ComponentWithViewTracking
          Component={Component}
          analyticsName={analyticsName}
          eventName={href ? 'linkViewEvent' : 'buttonViewEvent'}
          ref={ref}
        />
      );
    }

    return Component;
  });
};
